import Position from '../helpers/Position';
import General from '../helpers/General';
import Stats from '../helpers/Stats';
const $_element = document.querySelector('.twc-nav');
const MOBILE_ACTIVE_PATH_CLASS = 'twc-mnav-link-active-path';
const MOBILE_DESKTOP_LEVEL3_CLASS =
  'twc-mnav-subview-desktop-level3';
const NAV_WRAPPER_OPEN_CLASSES = ['visible', 'right-0', 'opacity-100'];
const NAV_WRAPPER_CLOSED_CLASSES = ['invisible', 'opacity-0'];
const HEADER_SECTION_SELECTOR =
  ':is(#shopify-section-header, #shopify-section-header-v2)';

export default class Nav {
  constructor() {
    this.listenerOpenTab = false;
    this.state = 'close';
    this.mobileLevel3PendingByScope = new WeakMap();
    this.mobileNavV2TrackingBound = false;
    this.mobileNavV2GeneralTrackingBound = false;
    this.deferredNavFetchPromise = null;
    this.deferredNavHtml = null;
    this.deferredNavHtmlSectionId = null;
    this.deferredNavLoadPromise = null;
    this.deferredNavOpenRequested = false;

    this.setNavLoclStorage();
    this.disableLegacyMobileHamburgerTrackingForV2();

    this.toggleNav();
    this.toggleNavSpecial();
    this.handleClickCloseButton();
    this.handleClickWrapper();

    this.initialEvents();

    if (window.nav_progressbar !== undefined) {
      this.progressBarMobileSiteNav();
    }

    this.bindViewEvents();
    this.bindMobileNewNavHeader();
    this.bindKeyboardDropdown();
    this.bindKeyboardSiteNavLinks();
    this.bindMobileNavV2GeneralTracking();
    this.bindMobileNavV2Tracking();
    this.bindDeferredNavLoading();
    this.scheduleDeferredNavPrefetch();
  }

  initialEvents(scope = document) {
    const event_links = scope.querySelectorAll('.twc-hdr-event-link');
    event_links.forEach((event_link) => {
      if (event_link.dataset.navStatsBound === 'true') {
        return;
      }
      event_link.dataset.navStatsBound = 'true';
      event_link.addEventListener('click', (e) => {
        const link = e.currentTarget;
        let type = link.dataset.navType;
        const title = link.getAttribute('title');
        const nav = link.dataset.navMode;

        if (
          nav === 'Old' &&
          type === 'Desktop Link' &&
          window.matchMedia('(max-width: 1023.98px)').matches
        ) {
          type = 'Hot Bar Link';
        }
        this.sendStats(
          'send',
          'event',
          `${nav} Navigation`,
          'Click',
          `${type} ${title}`
        );
      });
    });
  }

  sendStats() {
    if (window.ga) {
      ga(arguments);
    }
  }

  isNewNavV2Active() {
    return document.documentElement.classList.contains('twc-nav-enabled');
  }

  isDesktopSidebarNavActive() {
    const isMobileViewport = window.matchMedia
      ? window.matchMedia('(max-width: 1023.98px)').matches
      : window.innerWidth < 1024;

    return (
      !isMobileViewport &&
      document.documentElement.classList.contains(
        'twc-nav-desktop-sidebar-enabled'
      )
    );
  }

  setTailwindDrawerState(navWrapper, isOpen) {
    if (!navWrapper) {
      return;
    }

    navWrapper.classList.remove(
      ...(isOpen ? NAV_WRAPPER_CLOSED_CLASSES : NAV_WRAPPER_OPEN_CLASSES)
    );
    navWrapper.classList.add(
      ...(isOpen ? NAV_WRAPPER_OPEN_CLASSES : NAV_WRAPPER_CLOSED_CLASSES)
    );
    navWrapper.dataset.navState = isOpen ? 'open' : 'closed';
  }

  setTailwindToggleState(toggleButton, isOpen) {
    if (!toggleButton) {
      return;
    }

    const spans = toggleButton.querySelectorAll('span');
    spans[0]?.classList.toggle('top-2', isOpen);
    spans[0]?.classList.toggle('rotate-45', isOpen);
    spans[1]?.classList.toggle('-rotate-45', isOpen);
    spans[2]?.classList.toggle('opacity-0', isOpen);
    toggleButton.dataset.navToggleState = isOpen ? 'open' : 'closed';
  }

  getDeferredNavWrapper(element = null) {
    const sectionWrapper = this.getActiveSectionWrapper(element);
    return (
      sectionWrapper?.querySelector('[data-nav-deferred]') ||
      document.querySelector('[data-nav-deferred]') ||
      null
    );
  }

  buildDeferredNavUrl(sectionId) {
    const rootPath = window.Shopify?.routes?.root || '/';
    const separator = rootPath.includes('?') ? '&' : '?';

    return `${rootPath}${separator}section_id=${encodeURIComponent(
      sectionId
    )}&content=1`;
  }

  initializeDeferredNavContent(wrapper) {
    if (!wrapper) {
      return;
    }

    this.initialEvents(wrapper);
    this.disableLegacyMobileHamburgerTrackingForV2(wrapper);
    this.bindNavCloseButtons(wrapper);
    this.handleClickWrapper(wrapper);
    this.bindMobileNewNavHeader(wrapper);
    this.bindKeyboardDropdown(wrapper);
    this.bindKeyboardSiteNavLinks(wrapper);
    this.bindViewEvents(wrapper);
  }

  fetchDeferredNav(sectionId, lowPriority = false) {
    if (
      this.deferredNavHtml &&
      this.deferredNavHtmlSectionId === sectionId
    ) {
      return Promise.resolve(this.deferredNavHtml);
    }

    if (this.deferredNavFetchPromise) {
      return this.deferredNavFetchPromise;
    }

    this.deferredNavFetchPromise = fetch(this.buildDeferredNavUrl(sectionId), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      priority: lowPriority ? 'low' : 'auto',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Deferred navigation request failed: ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        this.deferredNavHtml = html;
        this.deferredNavHtmlSectionId = sectionId;
        this.deferredNavFetchPromise = null;
        return html;
      })
      .catch((error) => {
        this.deferredNavFetchPromise = null;
        throw error;
      });

    return this.deferredNavFetchPromise;
  }

  mountDeferredNav(html, placeholder, sectionId, sectionWrapper) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextWrapper = parsedDocument.querySelector('#nav-main-wrapper');

    if (!nextWrapper) {
      throw new Error(
        'Deferred navigation response did not contain #nav-main-wrapper'
      );
    }

    nextWrapper.setAttribute('aria-busy', 'false');
    placeholder.replaceWith(nextWrapper);

    const sourceId = `new-nav-theme-block-source-${sectionId}`;
    const themeBlockSource = parsedDocument.getElementById(sourceId);
    if (themeBlockSource && !document.getElementById(sourceId)) {
      (sectionWrapper || document.body).appendChild(themeBlockSource);
    }

    this.initializeDeferredNavContent(nextWrapper);
    window.dispatchEvent(
      new CustomEvent('gld:deferred-nav-loaded', {
        detail: { sectionId, wrapper: nextWrapper },
      })
    );
    window.dispatchEvent(new Event('newnav:change'));

    this.deferredNavHtml = null;
    this.deferredNavHtmlSectionId = null;
    return nextWrapper;
  }

  loadDeferredNav(triggerElement = null) {
    const placeholder = this.getDeferredNavWrapper(triggerElement);
    if (!placeholder) {
      return Promise.resolve(this.getActiveNavWrapper(triggerElement));
    }

    if (this.deferredNavLoadPromise) {
      return this.deferredNavLoadPromise;
    }

    const sectionId = placeholder.dataset.navSectionId;
    if (!sectionId) {
      return Promise.resolve(null);
    }

    const sectionWrapper = this.getActiveSectionWrapper(triggerElement);
    const openToggle = this.getActiveOpenToggle(triggerElement);
    placeholder.dataset.navLoadState = 'loading';
    placeholder.setAttribute('aria-busy', 'true');
    openToggle?.setAttribute('aria-busy', 'true');

    this.deferredNavLoadPromise = this.fetchDeferredNav(sectionId)
      .then((html) => {
        const currentPlaceholder = this.getDeferredNavWrapper(triggerElement);
        if (!currentPlaceholder) {
          return this.getActiveNavWrapper(triggerElement);
        }

        return this.mountDeferredNav(
          html,
          currentPlaceholder,
          sectionId,
          sectionWrapper
        );
      })
      .catch((error) => {
        this.deferredNavHtml = null;
        this.deferredNavHtmlSectionId = null;
        const currentPlaceholder = this.getDeferredNavWrapper(triggerElement);
        if (currentPlaceholder) {
          currentPlaceholder.dataset.navLoadState = 'error';
          currentPlaceholder.setAttribute('aria-busy', 'false');
        }
        console.error('Mobile navigation could not be loaded.', error);
        return null;
      })
      .finally(() => {
        this.deferredNavLoadPromise = null;
        openToggle?.setAttribute('aria-busy', 'false');
      });

    return this.deferredNavLoadPromise;
  }

  bindDeferredNavLoading() {
    const preload = (event) => {
      this.loadDeferredNav(event.currentTarget);
    };

    document.querySelectorAll('.twc-nav-toggle-btn-open').forEach((toggle) => {
      if (toggle.dataset.navDeferredPreloadBound === 'true') {
        return;
      }
      toggle.dataset.navDeferredPreloadBound = 'true';
      toggle.addEventListener('pointerover', preload, { passive: true });
      toggle.addEventListener('focus', preload, { passive: true });
      toggle.addEventListener('touchstart', preload, { passive: true });
    });
  }

  scheduleDeferredNavPrefetch() {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (connection?.saveData) {
      return;
    }

    const prefetch = () => {
      const placeholder = this.getDeferredNavWrapper();
      const sectionId = placeholder?.dataset.navSectionId;
      if (!sectionId) {
        return;
      }

      this.fetchDeferredNav(sectionId, true).catch(() => {
        // Intent-based loading remains available and will retry the request.
      });
    };

    const scheduleWhenIdle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prefetch, { timeout: 4000 });
      } else {
        window.setTimeout(prefetch, 1500);
      }
    };

    if (document.readyState === 'complete') {
      scheduleWhenIdle();
    } else {
      window.addEventListener('load', scheduleWhenIdle, { once: true });
    }
  }

  getMobileNavV2TrackingPrefix() {
    return document.documentElement.classList.contains(
      'twc-nav-desktop-sidebar-enabled'
    )
      ? 'desktop-mobile-nav-v2'
      : 'mobile-nav-v2';
  }

  buildMobileNavV2Category(suffix = '') {
    return this.buildTrackingLabel([this.getMobileNavV2TrackingPrefix(), suffix], '__');
  }

  buildMobileNavV2EventLabel(suffix = '') {
    return this.buildMobileNavV2Category(suffix);
  }

  disableLegacyMobileHamburgerTrackingForV2(scope = document) {
    if (!this.isNewNavV2Active()) {
      return;
    }

    scope
      .querySelectorAll('.twc-nav-toggle-btn-open, .twc-nav-toggle-btn-close')
      .forEach((toggleButton) => {
        toggleButton.classList.remove('gld-gtag-event--click');
        toggleButton.removeAttribute('data-gtag-data');
      });
  }

  normalizeTrackingValue(value = '') {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  buildTrackingLabel(parts = [], delimiter = '_') {
    return parts
      .map((part) => this.normalizeTrackingValue(part))
      .filter((part) => part.length > 0)
      .join(delimiter);
  }

  sendTrackingEvent(event_category, event_label = '') {
    if (!event_category) {
      return;
    }

    const payload = { event_category };
    const normalizedLabel = this.normalizeTrackingValue(event_label);
    if (normalizedLabel) {
      payload.event_label = normalizedLabel;
    }

    Stats.sendEventDataLayerViaShopify(payload);
  }

  getElementHrefValue(element) {
    if (!element || typeof element.getAttribute !== 'function') {
      return '';
    }

    return this.normalizeTrackingValue(element.getAttribute('href') || '');
  }

  getFirstChildWithoutClasses(element, excludedClassNames = []) {
    if (!element || !element.children) {
      return null;
    }

    return (
      [...element.children].find((child) => {
        if (!child.classList) {
          return true;
        }

        return !excludedClassNames.some((className) =>
          child.classList.contains(className)
        );
      }) || null
    );
  }

  getMobileLinkValue(link) {
    if (!link) {
      return '';
    }

    const labelChild = this.getFirstChildWithoutClasses(link, [
      'twc-mnav-icon',
      'twc-mnav-badge',
      'twc-mnav-arrow',
      'twc-mnav-back-icon',
    ]);

    if (labelChild) {
      return this.normalizeTrackingValue(labelChild.textContent);
    }

    return this.normalizeTrackingValue(link.textContent);
  }

  getMobileSubviewTrackingContext(element) {
    const subview = element?.closest('.twc-mnav-hook-dy-subview');
    if (!subview) {
      return {
        level: null,
        parentLevel1: '',
        parentLevel2: '',
      };
    }

    const title = this.normalizeTrackingValue(
      subview.querySelector('.twc-mnav-title')?.textContent || ''
    );

    const backButton = subview.querySelector('.twc-mnav-hook-dy-link-back');
    const backLevel = parseInt(backButton?.dataset?.level || '', 10);
    const backSpans = backButton ? [...backButton.querySelectorAll('span')] : [];
    const backLabel = this.normalizeTrackingValue(
      backSpans[backSpans.length - 1]?.textContent ||
        backButton?.getAttribute('title') ||
        ''
    );

    if (backLevel === 3) {
      return {
        level: 3,
        parentLevel1: backLabel,
        parentLevel2: title,
      };
    }

    return {
      level: 2,
      parentLevel1: title,
      parentLevel2: '',
    };
  }

  getMobileBlockValue(blockLink) {
    if (!blockLink) {
      return '';
    }

    const imageTitle = blockLink.querySelector(
      '.twc-mnav-level2-image-title-inner'
    );
    if (imageTitle) {
      return this.normalizeTrackingValue(imageTitle.textContent);
    }

    const textValue = this.normalizeTrackingValue(blockLink.textContent);
    if (textValue) {
      return textValue;
    }

    return this.normalizeTrackingValue(
      blockLink.querySelector('img')?.getAttribute('alt') || ''
    );
  }

  bindMobileNavV2Tracking() {
    if (this.mobileNavV2TrackingBound) {
      return;
    }

    this.mobileNavV2TrackingBound = true;

    document.addEventListener(
      'click',
      (event) => {
        if (!this.isNewNavV2Active()) {
          return;
        }

        const trigger = event.target?.closest?.('a, button');
        if (!trigger) {
          return;
        }

        const mobileContainer = trigger.closest('.twc-mnav-container');
        if (!mobileContainer) {
          return;
        }

        const backLink = trigger.closest('.twc-mnav-hook-dy-link-back');
        if (backLink) {
          const labelSpans = [...backLink.querySelectorAll('span')];
          const backlinkValue = this.normalizeTrackingValue(
            labelSpans[labelSpans.length - 1]?.textContent ||
              backLink.getAttribute('title') ||
              ''
          );
          this.sendTrackingEvent(
            this.buildMobileNavV2Category('backlink'),
            backlinkValue
          );
          return;
        }

        const footerLink = trigger.closest('.twc-mnav-footer-link');
        if (footerLink) {
          const value = this.normalizeTrackingValue(footerLink.textContent);
          const href = this.getElementHrefValue(footerLink);
          this.sendTrackingEvent(
            this.buildMobileNavV2Category('level1__footer'),
            this.buildTrackingLabel([value, href])
          );
          return;
        }

        const carouselCard = trigger.closest('.twc-mcarousel-card');
        if (carouselCard) {
          const carouselIndex =
            carouselCard.closest('.twc-mcarousel')?.dataset
              ?.carouselIndex || '1';
          const value = this.normalizeTrackingValue(
            carouselCard.querySelector('.twc-mcarousel-card-title')
              ?.textContent ||
              carouselCard.querySelector('img')?.getAttribute('alt') ||
              ''
          );
          const href = this.getElementHrefValue(carouselCard);
          this.sendTrackingEvent(
            this.buildMobileNavV2Category(`level1__slider${carouselIndex}`),
            this.buildTrackingLabel([value, href])
          );
          return;
        }

        const blockLink = trigger.closest(
          '.twc-mnav-level2-image-card, .twc-mnav-icon-grid-card'
        );
        if (blockLink) {
          const value = this.getMobileBlockValue(blockLink);
          const { level, parentLevel1, parentLevel2 } =
            this.getMobileSubviewTrackingContext(blockLink);

          if (level === 3) {
            this.sendTrackingEvent(
              this.buildMobileNavV2Category('level3__block'),
              this.buildTrackingLabel([parentLevel1, parentLevel2, value])
            );
          } else {
            this.sendTrackingEvent(
              this.buildMobileNavV2Category('level2__block'),
              this.buildTrackingLabel([parentLevel1, value])
            );
          }
          return;
        }

        const navLink = trigger.closest('.twc-mnav-link');
        if (!navLink) {
          return;
        }

        const level = parseInt(navLink.dataset.level || '1', 10);
        const value = this.getMobileLinkValue(navLink);
        const isToggle = navLink.classList.contains('twc-mnav-hook-dy-link-view');
        const { parentLevel1, parentLevel2 } =
          this.getMobileSubviewTrackingContext(navLink);

        if (level === 1) {
          this.sendTrackingEvent(
            isToggle
              ? this.buildMobileNavV2Category('level1__toggle')
              : this.buildMobileNavV2Category('level1__item'),
            value
          );
          return;
        }

        if (level === 2) {
          this.sendTrackingEvent(
            isToggle
              ? this.buildMobileNavV2Category('level2__toggle')
              : this.buildMobileNavV2Category('level2__item'),
            this.buildTrackingLabel([parentLevel1, value])
          );
          return;
        }

        this.sendTrackingEvent(
          this.buildMobileNavV2Category('level3__item'),
          this.buildTrackingLabel([parentLevel1, parentLevel2, value])
        );
      },
      true
    );
  }

  bindMobileNavV2GeneralTracking() {
    if (this.mobileNavV2GeneralTrackingBound) {
      return;
    }

    this.mobileNavV2GeneralTrackingBound = true;

    document.addEventListener(
      'focusin',
      (event) => {
        if (!this.isNewNavV2Active()) {
          return;
        }

        const searchInput = event.target?.closest?.('.twc-mnav-hook-new-search-input');
        if (!searchInput) {
          return;
        }

        const mobileContainer = searchInput.closest('.twc-mnav-container');
        if (!mobileContainer) {
          return;
        }

        this.sendTrackingEvent(
          this.buildMobileNavV2Category('general'),
          this.buildMobileNavV2EventLabel('search')
        );
      },
      true
    );
  }

  getSectionWrapperForElement(element) {
    if (!element || typeof element.closest !== 'function') {
      return null;
    }

    const preferredWrapper = element.closest(HEADER_SECTION_SELECTOR);
    if (preferredWrapper) {
      return preferredWrapper;
    }

    const sectionWrapper = element.closest('.shopify-section');
    return sectionWrapper?.querySelector('header[data-header-section]')
      ? sectionWrapper
      : null;
  }

  getActiveSectionWrapper(element = null) {
    const sectionFromElement = this.getSectionWrapperForElement(element);
    if (sectionFromElement) {
      return sectionFromElement;
    }

    return (
      document.querySelector(HEADER_SECTION_SELECTOR) ||
      document
        .querySelector('header[data-header-section]')
        ?.closest('.shopify-section') ||
      null
    );
  }

  getActiveHeaderSection(element = null) {
    const sectionWrapper = this.getActiveSectionWrapper(element);
    const headerFromSection =
      sectionWrapper?.querySelector('header[data-header-section]');
    if (headerFromSection) {
      return headerFromSection;
    }

    const headerFromElement = element?.closest?.('header[data-header-section]');
    if (headerFromElement) {
      return headerFromElement;
    }

    const headerFromPreferredSection = document.querySelector(
      `${HEADER_SECTION_SELECTOR} header[data-header-section]`
    );

    if (headerFromPreferredSection) {
      return headerFromPreferredSection;
    }

    return document.querySelector('header[data-header-section]');
  }

  getActiveNavScope(element = null) {
    return this.getActiveSectionWrapper(element) || document;
  }

  getActiveNavWrapper(element = null) {
    const activeSectionWrapper = this.getActiveSectionWrapper(element);
    const wrapperFromActiveSection = activeSectionWrapper?.querySelector(
      '.twc-nav-wrapper'
    );

    return (
      wrapperFromActiveSection ||
      document.querySelector('.twc-nav-wrapper[data-nav-state="open"]') ||
      document.querySelector('.twc-nav-wrapper')
    );
  }

  getActiveOpenToggle(element = null) {
    const triggerOpenToggle = element?.closest?.('.twc-nav-toggle-btn-open');
    if (triggerOpenToggle) {
      return triggerOpenToggle;
    }

    const activeSectionWrapper = this.getActiveSectionWrapper(element);

    return (
      activeSectionWrapper?.querySelector('.twc-nav-toggle-btn-open') ||
      document.querySelector('.twc-nav-toggle-btn-open')
    );
  }

  setTopPosition(navWrapper = null) {
    const activeNavWrapper = navWrapper || this.getActiveNavWrapper();
    if (!activeNavWrapper) {
      return;
    }

    const $_breakpoint = document.querySelector(
      '#shopify-section-gld_promotion-bar'
    );
    const top = this.isDesktopSidebarNavActive()
      ? 0
      : $_breakpoint?.offsetHeight || 0;
    // activeNavWrapper.style.top = `${top}px`;
    activeNavWrapper.style.top = `0px`;
    // activeNavWrapper.style.height = `calc(100% - ${top}px)`;
    activeNavWrapper.style.height = `100%`;
  }

  setNavLoclStorage() {
    const nav = localStorage.getItem('gender');
    if (nav) {
      this.setSpecialNav(nav);
    }
  }

  bindNavOpenButtons(scope = document) {
    const $_btn_open = scope.querySelectorAll('.twc-nav-toggle-btn-open');

    $_btn_open.forEach((openButton) => {
      if (openButton.dataset.navOpenBound === 'true') {
        return;
      }
      openButton.dataset.navOpenBound = 'true';
      openButton.addEventListener('click', (e) => {
        this.openNav(e.currentTarget);
      });
    });
  }

  bindNavCloseButtons(scope = document) {
    const $_btn_close = scope.querySelectorAll(
      '.twc-nav-toggle-btn-close, .twc-mnav-hook-header-close'
    );

    $_btn_close.forEach((closeButton) => {
      if (closeButton.dataset.navCloseBound === 'true') {
        return;
      }
      closeButton.dataset.navCloseBound = 'true';
      closeButton.addEventListener('click', (e) => {
        const sectionWrapper = this.getSectionWrapperForElement(
          e.currentTarget
        );
        if (sectionWrapper) {
          sectionWrapper.classList.remove('twc-hdr-drawer-open');
        }
        this.closeNav(e.currentTarget);
      });
    });
  }

  toggleNav() {
    this.bindNavOpenButtons();
    this.bindNavCloseButtons();

    document.addEventListener(
      'click',
      (event) => {
        if (
          !this.isDesktopSidebarNavActive() ||
          !document.body.classList.contains('twc-nav-open')
        ) {
          return;
        }

        const target = event.target;
        const activeWrapper = this.getActiveNavWrapper(target);

        const navPanel = target?.closest?.(
          '.twc-nav-top, .twc-mnav-new-header, .twc-mnav'
        );

        if (
          !activeWrapper ||
          activeWrapper.dataset.navState !== 'open' ||
          (navPanel && activeWrapper.contains(navPanel)) ||
          target?.closest?.('.twc-nav-toggle-btn')
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.closeNav(target);
      },
      true
    );
  }

  toggleNavSpecial() {
    const $_btn = document.querySelector('.twc-hdr-sex');
    if ($_btn) {
      $_btn.addEventListener('click', (e) => {
        const { nav, href } = e.target.closest(
          '.twc-hdr-sex-item'
        ).dataset;
        if (nav) {
          localStorage.setItem('gender', nav);
          window.location.href = href;
        }
      });
    }
  }

  setSpecialNav(nav) {
    [...document.querySelectorAll('.twc-hdr-sex-item')].forEach(
      (item) => {
        if (item.dataset.nav !== nav) {
          item.classList.add('twc-nav-active');
        } else {
          item.classList.remove('twc-nav-active');
        }
      }
    );

    [...document.querySelectorAll('.twc-hdr-bottom .twc-site-nav')].forEach(
      (nav_item) => {
        if (nav_item.dataset.nav === nav) {
          nav_item.classList.add('twc-site-nav-active');
        } else {
          nav_item.classList.remove('twc-site-nav-active');
        }
      }
    );
  }

  handleClickCloseButton() {
    const $_btn = document.querySelectorAll('.twc-nav-btn-close');
    if ($_btn.length) {
      $_btn.forEach((closeButton) => {
        closeButton.addEventListener('click', (event) => {
          this.closeNav(event.currentTarget);
        });
      });
    }
  }

  closeNav(triggerElement = null) {
    const navWrapper = this.getActiveNavWrapper(triggerElement);
    const shouldTrackMobileV2Close =
      this.isNewNavV2Active() && navWrapper?.dataset.navState === 'open';
    if (shouldTrackMobileV2Close) {
      this.sendTrackingEvent(
        this.buildMobileNavV2Category('general'),
        this.buildMobileNavV2EventLabel('hamburger-closed')
      );
    }

    this.state = 'close';
    const activeSearchContainer = document.querySelector(
      '[data-search-container][data-search-state="active"]'
    );
    if (activeSearchContainer) {
      const searchCloseButton = document.querySelector(
        '[data-search-close]'
      );
      if (searchCloseButton) {
        searchCloseButton.click();
      } else {
        activeSearchContainer.dataset.searchState = 'inactive';
        document
          .querySelectorAll(HEADER_SECTION_SELECTOR)
          .forEach((sectionWrapper) => {
            sectionWrapper.dataset.searchOverlayState = 'inactive';
          });
      }
    }

    document.body.classList.remove('twc-nav-mobile-search-overlay-active');
    document.documentElement.classList.remove(
      'twc-nav-mobile-search-overlay-active'
    );
    if (this.isNewNavV2Active()) {
      const navScope = this.getActiveNavScope(triggerElement);
      const mobileNewNavInput =
        navScope.querySelector(
          '.twc-mnav-hook-new-search-input'
        ) || document.querySelector('.twc-mnav-hook-new-search-input');
      if (mobileNewNavInput) {
        mobileNewNavInput.value = '';
      }

      window.dispatchEvent(new Event('new-nav-search-reset'));
    }
    this.sendStats(
      'send',
      'event',
      'New Navigation',
      'Click',
      'Close Hamburger'
    );
    const openToggle = this.getActiveOpenToggle(triggerElement);

    const sectionWrapperFromTrigger =
      this.getSectionWrapperForElement(triggerElement);
    if (sectionWrapperFromTrigger) {
      sectionWrapperFromTrigger.classList.remove('twc-hdr-drawer-open');
    } else {
      document
        .querySelectorAll(HEADER_SECTION_SELECTOR)
        .forEach((sectionWrapper) => {
          sectionWrapper.classList.remove('twc-hdr-drawer-open');
        });
    }

    General.bodyOverflowHiddenController('main-nav', false, false);
    if (navWrapper) {
      this.setTailwindDrawerState(navWrapper, false);
    }
    document.querySelector('body').classList.remove('overflow-hidden');
    document.querySelector('body').classList.remove('twc-nav-open');
    if (openToggle) {
      this.setTailwindToggleState(openToggle, false);
    }
    document
      .querySelectorAll('.twc-nav-toggle-btn-open[data-nav-toggle-state="open"]')
      .forEach((toggleButton) => {
        if (toggleButton !== openToggle) {
          this.setTailwindToggleState(toggleButton, false);
        }
      });

    this.resetMobileViewState(triggerElement);
  }

  openNav(triggerElement = null) {
    const navWrapper = this.getActiveNavWrapper(triggerElement);
    if (!navWrapper) {
      return;
    }

    if (navWrapper.hasAttribute('data-nav-deferred')) {
      if (this.deferredNavOpenRequested) {
        return;
      }

      this.deferredNavOpenRequested = true;
      this.loadDeferredNav(triggerElement).then((loadedWrapper) => {
        this.deferredNavOpenRequested = false;
        if (loadedWrapper) {
          this.openNav(triggerElement);
        } else {
          this.getActiveSectionWrapper(triggerElement)?.classList.remove(
            'twc-hdr-drawer-open'
          );
        }
      });
      return;
    }

    this.getSectionWrapperForElement(triggerElement)?.classList.add(
      'twc-hdr-drawer-open'
    );

    if (this.isNewNavV2Active()) {
      this.sendTrackingEvent(
        this.buildMobileNavV2Category('general'),
        this.buildMobileNavV2EventLabel('hamburger-open')
      );
    }

    this.state = 'open';
    this.resetMobileViewState(triggerElement);
    this.setTopPosition(navWrapper);
    this.sendStats(
      'send',
      'event',
      'New Navigation',
      'Click',
      'Open Hamburger'
    );

    setTimeout(() => {
      General.bodyOverflowHiddenController('main-nav', true, false);
      this.setTailwindDrawerState(navWrapper, true);
      document.querySelector('body').classList.add('overflow-hidden');
      document.querySelector('body').classList.add('twc-nav-open');
      const openToggle = this.getActiveOpenToggle(triggerElement);
      if (openToggle) {
        this.setTailwindToggleState(openToggle, true);
      }
      if (!this.listenerOpenTab) {
        this.listenerFunctionOpenTab();
      }
      window.requestAnimationFrame(() => {
        this.syncMobileNewNavHeaderState(triggerElement);
      });
    }, 100);
  }

  listenerFunctionOpenTab() {
    document.addEventListener(
      'bodyOverflowHiddenController',
      () => {
        let overflowModules = [];
        try {
          overflowModules =
            JSON.parse(sessionStorage.getItem('overflow_hidden_modules')) || [];
        } catch (error) {
          overflowModules = [];
        }

        const keepOpenForNewNavSearchOverlay = document.body.classList.contains(
          'twc-nav-mobile-search-overlay-active'
        );

        if (
          overflowModules.includes('main-nav') &&
          overflowModules.length > 1 &&
          this.state === 'open' &&
          !keepOpenForNewNavSearchOverlay
        ) {
          this.closeNav();
        }
      },
      false
    );
    this.listenerOpenTab = true;
  }

  handleClickWrapper(scope = document) {
    const wrappers = scope.matches?.('.twc-nav-wrapper')
      ? [scope]
      : scope.querySelectorAll('.twc-nav-wrapper');
    if (wrappers.length) {
      wrappers.forEach((wrapper) => {
        if (wrapper.dataset.navWrapperClickBound === 'true') {
          return;
        }
        wrapper.dataset.navWrapperClickBound = 'true';
        wrapper.addEventListener('click', (e) => {
          if (e.target === wrapper && wrapper.dataset.navState === 'open') {
            this.closeNav(wrapper);
          }
        });
      });
    }
  }

  progressBarMobileSiteNav() {
    const nav =
      document.querySelector(
        '.twc-hdr-nav-variant-active .twc-site-nav.twc-site-nav-mobile'
      ) || document.querySelector('.twc-site-nav.twc-site-nav-mobile');
    if (!nav) {
      return;
    }
    nav.classList.add('twc-site-nav-progressbar');
    const items = [...nav.children];
    let arrow = false;

    let value_total = 0;
    items.forEach((item) => (value_total += item.offsetWidth));
    value_total -= window.outerWidth;
    const setValue = (event) => {
      const value_current = event.target.scrollLeft;
      let value_percent = Math.ceil((value_current * 100) / value_total);
      if (value_percent > 100) {
        value_percent = 100;
      }
      setProgressbar(value_percent);
      if (arrow) {
        setArrow(value_percent);
      }
    };

    const setProgressbar = (value) => {
      const progressbar_value_element = document.querySelector(
        '.twc-site-nav-progressbar-value'
      );
      if (progressbar_value_element) {
        progressbar_value_element.style.width = `${value}%`;
      }
    };

    const setArrow = General.debounce((value) => {
      const hide_arrow = arrow.classList.contains('twc-site-nav-arrow-hide');
      if (value > 90 && !hide_arrow) {
        arrow.classList.add('twc-site-nav-arrow-hide');
      } else if (value <= 90 && hide_arrow) {
        arrow.classList.remove('twc-site-nav-arrow-hide');
      }
    }, 500);

    if (nav) {
      nav.addEventListener('scroll', (event) => setValue(event));
      nav.insertAdjacentHTML(
        'afterend',
        '<div class=" absolute bottom-0 left-0 h-[3px] w-full md:hidden"><div class="twc-site-nav-progressbar-value h-full w-0 bg-gld-gold transition-all duration-100"></div></div>'
      );
      nav.insertAdjacentHTML(
        'afterend',
        '<div class="twc-site-nav-arrow absolute right-0 bottom-0 flex h-[50px] w-10 items-center justify-center bg-[linear-gradient(90deg,transparent_0%,#161616_50%,#161616_100%)] text-gld-white md:hidden [&.twc-site-nav-arrow-hide]:hidden"><svg aria-hidden="true" class="icon ml-2.5 inline-block size-4 align-middle fill-current icon-chevron-right" focusable="false" role="presentation" viewBox="0 0 14 14"><path d="M3.871.604c.44-.439 1.152-.439 1.591 0l5.515 5.515s-.049-.049.003.004l.082.08c.439.44.44 1.153 0 1.592l-5.6 5.6a1.125 1.125 0 0 1-1.59-1.59L8.675 7 3.87 2.195a1.125 1.125 0 0 1 0-1.59z" fill="#fff"></path></svg></div>'
      );
      arrow = document.querySelector('.twc-site-nav-arrow');

      arrow.addEventListener('click', () => {
        let value_px = Math.ceil((value_total * 20) / 100);
        nav.scrollLeft += value_px;
      });
    }
  }

  bindMobileNewNavHeader(scope = document) {
    const headerBackButtons = scope.querySelectorAll('.twc-mnav-hook-header-back');
    if (!headerBackButtons.length) {
      return;
    }

    headerBackButtons.forEach((headerBack) => {
      if (headerBack.dataset.navHeaderBackBound === 'true') {
        return;
      }
      headerBack.dataset.navHeaderBackBound = 'true';
      headerBack.addEventListener('click', (e) => {
        e.preventDefault();
        const currentLevel = this.getMobileCurrentLevel(headerBack);

        if (currentLevel <= 1) {
          return;
        }

        const activeSubview = this.getActiveMobileSubview(currentLevel, headerBack);
        const legacyBackButton = activeSubview?.querySelector(
          `.twc-mnav-hook-dy-link-back[data-level="${currentLevel}"]`
        );

        if (legacyBackButton) {
          legacyBackButton.click();
          return;
        }

        this.moveViewToLevel(currentLevel - 1, headerBack);
      });
    });

    this.syncMobileNewNavHeaderState();
  }

  getMobileCurrentLevel(contextElement = null) {
    const navScope = this.getActiveNavScope(contextElement);
    const view =
      navScope.querySelector('.twc-mnav-hook-dy-view') ||
      document.querySelector('.twc-mnav-hook-dy-view');

    if (!view) {
      return 1;
    }

    return parseInt(view.dataset.currentLevel || '1', 10);
  }

  getActiveMobileSubview(level, contextElement = null) {
    const navScope = this.getActiveNavScope(contextElement);
    const visibleSubviews = navScope
      ? [
          ...navScope.querySelectorAll(
            '.twc-mnav-hook-dy-subview:not(.twc-nav-hidden)'
          ),
        ]
      : [...document.querySelectorAll('.twc-mnav-hook-dy-subview:not(.twc-nav-hidden)')];
    const matchingSubviews = visibleSubviews.filter((subview) => {
      const ownBackButton = [...subview.children].find(
        (child) =>
          child.classList?.contains('twc-mnav-hook-dy-link-back') &&
          child.dataset?.level === `${level}`
      );

      return Boolean(ownBackButton);
    });

    return matchingSubviews[matchingSubviews.length - 1] || null;
  }

  getDirectMobileSubview(menuItem) {
    if (!menuItem || !menuItem.children) {
      return null;
    }

    return (
      [...menuItem.children].find(
        (child) => child.classList?.contains('twc-mnav-hook-dy-subview')
      ) || null
    );
  }

  clearMobileLevel3BranchState(navScope = null) {
    const scope = navScope || document;
    const preserveDesktopLevel3Position = this.isDesktopSidebarNavActive();

    scope
      .querySelectorAll('.twc-mnav-hook-dy-subview.twc-nav-has-active-level3')
      .forEach((subview) => {
        subview.classList.remove('twc-nav-has-active-level3');
      });
    scope
      .querySelectorAll('.twc-mnav-item.twc-nav-level3-branch')
      .forEach((item) => {
        item.classList.remove('twc-nav-level3-branch');
      });
    scope
      .querySelectorAll('.twc-mnav-hook-dy-subview.twc-nav-active-level3')
      .forEach((subview) => {
        subview.classList.remove('twc-nav-active-level3');
      });
    scope
      .querySelectorAll(`.${MOBILE_DESKTOP_LEVEL3_CLASS}`)
      .forEach((subview) => {
        if (!preserveDesktopLevel3Position) {
          subview.classList.remove(MOBILE_DESKTOP_LEVEL3_CLASS);
        }
      });
  }

  clearMobileActivePath(navScope = null, minLevel = 1) {
    const scope = navScope || document;
    scope
      .querySelectorAll(`.${MOBILE_ACTIVE_PATH_CLASS}`)
      .forEach((activeLink) => {
        const activeLevel = parseInt(activeLink.dataset?.level || '1', 10);
        if (Number.isNaN(activeLevel) || activeLevel >= minLevel) {
          activeLink.classList.remove(MOBILE_ACTIVE_PATH_CLASS);
        }
      });
  }

  setMobileActivePath(link) {
    if (!link) {
      return;
    }

    const navScope = this.getActiveNavScope(link);
    const level = parseInt(link.dataset?.level || '1', 10);

    this.clearMobileActivePath(navScope, Number.isNaN(level) ? 1 : level);
    link.classList.add(MOBILE_ACTIVE_PATH_CLASS);
  }

  clearPendingMobileLevel3BranchState(navScope = null) {
    const scope = navScope || document;
    const pendingState = this.mobileLevel3PendingByScope.get(scope);
    if (!pendingState) {
      return;
    }

    const { view, onTransitionEnd, timeoutId } = pendingState;
    if (view && onTransitionEnd) {
      view.removeEventListener('transitionend', onTransitionEnd);
    }
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }

    this.mobileLevel3PendingByScope.delete(scope);
  }

  setMobileLevel3BranchState(targetSubview, contextElement = null) {
    if (!targetSubview) {
      return;
    }

    const navScope = this.getActiveNavScope(contextElement);
    this.clearMobileLevel3BranchState(navScope);

    const branchItem = targetSubview.parentElement;
    const parentSubview = branchItem?.closest('.twc-mnav-hook-dy-subview');

    if (parentSubview) {
      parentSubview.classList.add('twc-nav-has-active-level3');
    }

    if (branchItem?.classList?.contains('twc-mnav-item')) {
      branchItem.classList.add('twc-nav-level3-branch');
    }

    targetSubview.classList.add('twc-nav-active-level3');
    targetSubview.classList.add(MOBILE_DESKTOP_LEVEL3_CLASS);
  }

  setMobileLevel3BranchStateAfterTransition(
    targetSubview,
    contextElement = null
  ) {
    if (!targetSubview) {
      return;
    }

    const navScope = this.getActiveNavScope(contextElement);
    const view =
      navScope.querySelector('.twc-mnav-hook-dy-view') ||
      document.querySelector('.twc-mnav-hook-dy-view');

    if (!view) {
      this.setMobileLevel3BranchState(targetSubview, contextElement);
      return;
    }

    this.clearPendingMobileLevel3BranchState(navScope);

    const applyBranchState = () => {
      // Transition may have been canceled by fast back / close nav interactions.
      if (
        view.dataset.currentLevel !== '3' ||
        targetSubview.classList.contains('twc-nav-hidden')
      ) {
        return;
      }

      this.setMobileLevel3BranchState(targetSubview, contextElement);
    };

    const finishPendingState = () => {
      this.clearPendingMobileLevel3BranchState(navScope);
      applyBranchState();
    };

    const onTransitionEnd = (event) => {
      if (event.target !== view || event.propertyName !== 'transform') {
        return;
      }
      finishPendingState();
    };

    view.addEventListener('transitionend', onTransitionEnd);

    const timeoutId = window.setTimeout(() => {
      finishPendingState();
    }, 380);

    this.mobileLevel3PendingByScope.set(navScope, {
      view,
      onTransitionEnd,
      timeoutId,
    });
  }

  resetMobileViewState(contextElement = null) {
    const navScope = this.getActiveNavScope(contextElement);
    if (!navScope) {
      return;
    }

    const view = navScope.querySelector('.twc-mnav-hook-dy-view');
    if (view) {
      view.dataset.currentLevel = '1';
    }

    const container = navScope.querySelector(
      '.twc-mnav-container'
    );
    if (container) {
      container.dataset.mobileCurrentLevel = '1';
    }

    this.clearPendingMobileLevel3BranchState(navScope);
    this.clearMobileLevel3BranchState(navScope);
    this.clearMobileActivePath(navScope);

    navScope
      .querySelectorAll('.twc-mnav-hook-dy-subview')
      .forEach((subview) => {
        subview.classList.add('twc-nav-hidden');
      });

    const scrollArea = navScope.querySelector(
      '.twc-mnav-hook-dy-scrollarea'
    );
    if (scrollArea) {
      scrollArea.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }

    this.syncMobileNewNavHeaderState(contextElement);
  }

  syncMobileNewNavHeaderState(contextElement = null) {
    const navScope = this.getActiveNavScope(contextElement);
    if (!navScope) {
      return;
    }

    const container = navScope.querySelector(
      '.twc-mnav-container'
    );
    const headerBackLabel = navScope.querySelector(
      '.twc-mnav-hook-header-back-label'
    );
    const headerTitle = navScope.querySelector(
      '.twc-mnav-hook-header-title'
    );
    const level = this.getMobileCurrentLevel(contextElement);

    if (container) {
      container.dataset.mobileCurrentLevel = `${level}`;
    }

    if (!headerBackLabel || !headerTitle) {
      return;
    }

    if (level <= 1) {
      headerBackLabel.textContent = 'All';
      headerTitle.textContent = '';
      headerTitle.classList.remove('twc-mnav-new-title-compact');
      return;
    }

    const activeSubview = this.getActiveMobileSubview(level, contextElement);
    const legacyBackLabel = activeSubview?.querySelector(
      `.twc-mnav-hook-dy-link-back[data-level="${level}"] span:last-child`
    );
    const legacyTitle = activeSubview?.querySelector('.twc-mnav-title');

    const nextBackLabel = legacyBackLabel?.textContent?.trim() || 'All';
    const nextHeaderTitle = legacyTitle?.textContent?.trim() || '';

    headerBackLabel.textContent = nextBackLabel;
    headerTitle.textContent = nextHeaderTitle;
    headerTitle.classList.toggle(
      'twc-mnav-new-title-compact',
      nextHeaderTitle.length > 20
    );
  }

  bindKeyboardDropdown(scope = document) {
    const dropdownItems = scope.querySelectorAll(
      '.twc-site-nav .twc-site-nav-item[aria-haspopup=true]'
    );

    dropdownItems.forEach((item) => {
      if (item.dataset.navKeyboardDropdownBound === 'true') {
        return;
      }
      item.dataset.navKeyboardDropdownBound = 'true';
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();

          const isOpen = item.classList.contains('twc-nav-active');

          dropdownItems.forEach((otherItem) => {
            otherItem.classList.remove('twc-nav-active');
            otherItem.setAttribute('aria-expanded', 'false');
          });

          if (!isOpen) {
            item.classList.add('twc-nav-active');
            item.setAttribute('aria-expanded', 'true');
          }
        }

        if (e.key === 'Escape') {
          item.classList.remove('twc-nav-active');
          item.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  bindKeyboardSiteNavLinks(scope = document) {
    const siteNavLinks = scope.querySelectorAll(
      '.twc-site-nav-level-2 .twc-site-nav-link'
    );
    siteNavLinks.forEach((link) => {
      if (link.dataset.navKeyboardLinkBound === 'true') {
        return;
      }
      link.dataset.navKeyboardLinkBound = 'true';
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          link.click();
        }
      });
    });
  }

  bindViewEvents(scope = document) {
    const menuItems = scope.querySelectorAll('.twc-mnav-hook-dy-link-view');

    menuItems.forEach((link) => {
      if (link.dataset.navViewBound === 'true') {
        return;
      }
      link.dataset.navViewBound = 'true';
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const targetLevel = parseInt(link.dataset.level, 10) + 1;
        if (Number.isNaN(targetLevel)) {
          return;
        }

        const navScope = this.getActiveNavScope(link);
        const isDesktopSidebarNav = this.isDesktopSidebarNavActive();
        const parentItem = link.parentElement;
        const targetSubview = this.getDirectMobileSubview(parentItem);
        const parentList = parentItem?.parentElement;

        // Keep only one opened subview per sibling set to avoid stacked level views.
        if (parentList) {
          [...parentList.children].forEach((siblingItem) => {
            if (siblingItem === parentItem) {
              return;
            }

            const siblingSubview = this.getDirectMobileSubview(siblingItem);
            if (siblingSubview) {
              siblingSubview.classList.add('twc-nav-hidden');
            }
          });
        }

        if (targetLevel < 3) {
          this.clearPendingMobileLevel3BranchState(navScope);
          this.clearMobileLevel3BranchState(navScope);
        }

        this.setMobileActivePath(link);

        if (targetSubview) {
          if (targetLevel === 3) {
            if (isDesktopSidebarNav) {
              this.setMobileLevel3BranchState(targetSubview, link);
            } else {
              this.setMobileLevel3BranchStateAfterTransition(targetSubview, link);
            }
          }
          targetSubview.classList.remove('twc-nav-hidden');
        }
        this.moveViewToLevel(targetLevel, link);
      });
    });

    const menuItemsBack = scope.querySelectorAll(
      '.twc-mnav-hook-dy-link-back'
    );

    menuItemsBack.forEach((link) => {
      if (link.dataset.navBackBound === 'true') {
        return;
      }
      link.dataset.navBackBound = 'true';
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const targetLevel = parseInt(link.dataset.level, 10) - 1;
        if (Number.isNaN(targetLevel)) {
          return;
        }

        if (targetLevel < 3) {
          const navScope = this.getActiveNavScope(link);
          this.clearPendingMobileLevel3BranchState(navScope);
          this.clearMobileLevel3BranchState(navScope);
        }

        const currentLevel = parseInt(link.dataset.level, 10);
        this.clearMobileActivePath(
          this.getActiveNavScope(link),
          Number.isNaN(currentLevel) ? targetLevel + 1 : currentLevel
        );
        this.moveViewToLevel(targetLevel, link);
        setTimeout(() => {
          if (link.parentElement?.classList?.contains('twc-mnav-hook-dy-subview')) {
            link.parentElement.classList.add('twc-nav-hidden');
          }
        }, 700);
      });
    });

  }

  moveViewToLevel(level, contextElement = null) {
    const navScope = this.getActiveNavScope(contextElement);
    const view =
      navScope.querySelector('.twc-mnav-hook-dy-view') ||
      document.querySelector('.twc-mnav-hook-dy-view');

    if (view) {
      view.dataset.currentLevel = level;
    }

    const container =
      navScope.querySelector('.twc-mnav-container') ||
      document.querySelector('.twc-mnav-container');
    if (container) {
      container.dataset.mobileCurrentLevel = `${level}`;
    }

    const scrollArea =
      navScope.querySelector('.twc-mnav-hook-dy-scrollarea') ||
      document.querySelector('.twc-mnav-hook-dy-scrollarea');
    if (scrollArea) {
      scrollArea.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }

    const activeSubview = this.getActiveMobileSubview(level, contextElement);
    if (activeSubview) {
      activeSubview.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }

    this.syncMobileNewNavHeaderState(contextElement);
  }
}
